import { useTranslation } from "react-i18next";
import Modal from "../Modal/Modal";
import { SOURCES_DEFAULT_ICON } from "src/helpers/constants";
import {
  MdClose,
  MdEdit,
  MdInfoOutline,
  MdOutlineShare,
  MdStarOutline,
} from "react-icons/md";
import { memo, useMemo } from "react";
import useAllTransactions from "src/hooks/useAllTransactions";
import { ensureDot, rgbaObjectToString } from "src/helpers/utils";
import clsx from "clsx";
import Icon from "../Icon/Icon";
import useMobile from "src/hooks/useMobile";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import ContactCards from "src/pages/Sources/ContactCards";
import { motion, useReducedMotion } from "framer-motion";
import ShinyCard from "../BankCard/ShinyCard";
import useModalsStore from "src/store/modalsStore";
import SmartImage from "../SmartImage/SmartImage";
import CollapsibleBlock from "../CollapsibleBlock/CollapsibleBlock";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind } from "../Button/Button.types";

interface IClientProviderInfoModal {
  onClose: () => void;
  clientProvider: string;
}

const ClientProviderInfoModal: React.FC<IClientProviderInfoModal> = ({
  onClose,
  clientProvider,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const { t } = useTranslation(["common", "modals"]);
  const { setShowEditClientProviderModal } = useModalsStore();
  const { isMobile } = useMobile();
  const { getSourceCustomSettings, isLoadingCustomizedSources } =
    useAllTransactions();

  const {
    icon,
    picture,
    iconStyles,
    contactData,
    address,
    bg,
    color,
    favorite,
  } = useMemo(() => {
    const res = getSourceCustomSettings(clientProvider);

    return {
      icon: res?.icon || SOURCES_DEFAULT_ICON,
      picture: res?.picture || null,
      iconStyles: {
        background: rgbaObjectToString(res?.bg),
        color: rgbaObjectToString(res?.color),
        border: `solid 1px ${rgbaObjectToString(res?.color)}`,
      },
      color: res.color,
      bg: res.bg,
      address: res?.address
        ? Array.isArray(res?.address)
          ? ""
          : res?.address
        : "",
      contactData: res?.contact || [],
      favorite: res.favorite,
    };
  }, [getSourceCustomSettings, clientProvider, isLoadingCustomizedSources]);

  const openEdit = () => {
    setShowEditClientProviderModal(true, {
      name: clientProvider,
      background: bg,
      color: color,
      icon: icon,
      image: picture,
      address,
      contact: contactData,
      favorite,
    });
  };

  return (
    <Modal
      onEscape={() => onClose()}
      className={clsx(["md:!max-w-[400px]", "md:!w-full"])}
    >
      <Modal.Header
        title={t("modals:contact.title")}
        onClose={() => onClose()}
      />
      <Modal.Body>
        <div
          className={clsx([
            "flex",
            "flex-col",
            "gap-4",
            "w-full",
            "md:!max-h-[370px]",
          ])}
        >
          <div className={clsx(["flex", "flex-row", "gap-2", "md:gap-4"])}>
            <motion.div
              className={clsx([
                "flex",
                "items-center",
                "justify-start",
                "!min-h-[64px]",
                "md:!min-h-[96px]",
                "!max-w-[64px]",
                "!min-w-[64px]",
                "md:!max-w-[96px]",
                "md:!min-w-[96px]",
                "shrink-0",
              ])}
              {...(!prefersReducedMotion && {
                initial: { opacity: 0 },
                exit: { opacity: 0 }, // cuadrado pequeño
                animate: { opacity: 1 }, // completamente visible
                transition: { duration: 0.5, ease: "easeInOut" },
              })}
            >
              <ShinyCard
                className={clsx([
                  "md:!rounded-full",
                  picture?.thumbnailUrl && [
                    "border",
                    "border-gray-50",
                    "dark:border-gray-900/50",
                  ],
                ])}
              >
                {picture?.thumbnailUrl ? (
                  <SmartImage
                    src={picture.thumbnailUrl}
                    className={clsx([
                      "rounded-full",
                      "!min-h-[62px]",
                      "!max-w-[62px]",
                      "!min-w-[62px]",
                      "md:!min-h-[96px]",
                      "md:!max-w-[96px]",
                      "md:!min-w-[96px]",
                      "bg-checkers",
                      "cursor-pointer",
                    ])}
                    onClick={() => openEdit()}
                  />
                ) : (
                  <Icon
                    name={icon}
                    size={isMobile ? ICON_SIZES.XL : ICON_SIZES.XL * 1.5}
                    className={clsx([
                      "rounded-full",
                      "p-2",
                      "md:p-2",
                      "cursor-pointer",
                    ])}
                    style={iconStyles}
                    onClick={() => openEdit()}
                  />
                )}
              </ShinyCard>
            </motion.div>
            <div
              className={clsx([
                "items-start",
                "flex",
                "w-full",
                "text-xs",
                "md:text-xs",
                "flex-col",
                "h-full",
                "gap-2",
              ])}
            >
              <div className={clsx(["flex", "flex-col"])}>
                <div
                  className={clsx([
                    "dark:text-cyan-400",
                    "text-xs",
                    "md:text-sm",
                    "font-medium",
                    "flex",
                    "items-center",
                    "gap-1",
                  ])}
                >
                  {favorite && (
                    <MdStarOutline
                      size={ICON_SIZES.SM}
                      style={{
                        ...iconStyles,
                        border: 0,
                      }}
                      className={clsx(["rounded", "p-[0.2em]"])}
                      title="Favorito"
                    />
                  )}
                  <span>{clientProvider}</span>
                </div>
                {address ? (
                  <div
                    className={clsx([
                      "font-light",
                      "w-full",
                      "text-xs",
                      "md:text-xs",
                    ])}
                  >
                    {ensureDot(address)}
                  </div>
                ) : (
                  <div className={clsx(["font-light", "w-full", "text-xs"])}>
                    {t("modals:contact.no_address")}{" "}
                  </div>
                )}
              </div>
              {contactData?.length ? null : (
                <div
                  className={clsx([
                    "flex-shrink",
                    "flex",
                    "text-xs",
                    "flex-grow",
                  ])}
                >
                  <div
                    className={clsx(["flex", "w-full", "flex-row", "gap-1"])}
                  >
                    <MdInfoOutline
                      size={ICON_SIZES.XS}
                      className={clsx(["opacity-30", "hidden"])}
                    />
                    {t("modals:contact.no_contact_info")}
                  </div>
                </div>
              )}
            </div>
          </div>
          {contactData?.length ? (
            <CollapsibleBlock
              title="Información de contacto"
              blocked
              open
              badge={`${contactData.length}`}
            >
              <ContactCards showCopy data={contactData} customPageSize={2} />
            </CollapsibleBlock>
          ) : null}
        </div>
      </Modal.Body>
      <Modal.Footer
        actions={[
          {
            text: t("common:buttons.close"),
            icon: <MdClose size={ICON_SIZES.SM} />,
            onClick: () => onClose(),
            kind: EButtonKind.NORMAL,
          },
        ]}
        leftContent={
          <ButtonGroup
            align="left"
            buttons={[
              {
                tooltip: t("common:tooltips.share"),
                icon: <MdOutlineShare size={ICON_SIZES.SM} />,
                onClick: async (e: any) => {
                  e?.preventDefault();
                  await navigator
                    .share({
                      text: [
                        clientProvider,
                        contactData
                          ?.map?.((info: any) => {
                            return `${info.name || "Sin nombre"}: ${
                              info.value
                            }`;
                          })
                          ?.join?.("\n"),
                      ].join("\n"),
                    })
                    .then((a) => {})
                    .catch((e) => {});
                },
                kind: EButtonKind.NORMAL,
                hide: !contactData?.length,
              },
              {
                tooltip: t("common:tooltips.edit"),
                icon: <MdEdit size={ICON_SIZES.SM} />,
                onClick: () => openEdit(),
                kind: EButtonKind.EDIT,
              },
            ]}
          />
        }
      />
    </Modal>
  );
};

export default memo(ClientProviderInfoModal);
